import { useState } from 'react';

import { Money } from '@betterment/core';
import { useFeatureFlag } from '@betterment/shared-components';

import { PerformanceLoadingState } from './loading-state/PerformanceLoadingState';

import { DateRangeOption } from '../../../../../../graphql/schema';
import { PerformanceSection } from '../../../../../../Shared/components-v2/charts/performance-chart/PerformanceSection';
import { DateRangeOptions } from '../../../../../../Shared/models/date-range-options';
import { urlBuilder } from '../../../../../../Shared/urlBuilder';
import { EnvelopeAccountPerformanceHistoryQuery } from '../../../graphql/envelopeAccountPerformanceHistory.generated';
import { useEnvelopePerformanceHistory } from '../../hooks/useEnvelopePerformanceHistory';

type ManagedAccountData = Extract<EnvelopeAccountPerformanceHistoryQuery['account'], { __typename: 'ManagedAccount' }>;

export const PerformanceChartSection = () => {
  const [dateRangeOption, setDateRangeOption] = useState<DateRangeOption>(DateRangeOption.AllTime);

  const { data: newData, loading, previousData } = useEnvelopePerformanceHistory(dateRangeOption);

  const { enabled: isReactifyPerformanceEnabled, loading: isFeatureFlagLoading } = useFeatureFlag({
    splitName: 'retail.user_level_performance_reactify_q3_2025_enabled',
    context: 'PerformanceChartSection',
  });

  const data = newData ?? previousData;
  const hasValidData = data && 'performanceHistory' in data.account;
  const managedAccount = hasValidData ? (data.account as ManagedAccountData) : null;

  if ((loading && !data) || isFeatureFlagLoading) return <PerformanceLoadingState />;
  if (!hasValidData || !managedAccount) return null;

  const { performanceHistory, legacyGoalId } = managedAccount;

  const balance = managedAccount?.balance ?? Money(0);
  const earned = managedAccount?.performanceTotals.investing?.earned ?? Money(0);

  const isLoadingWithPreviousData = loading && !newData && !!previousData;

  let detailsUrl: string | null = null;
  if (isReactifyPerformanceEnabled) {
    detailsUrl = urlBuilder.appAccountPerformance.absolute({
      accountId: managedAccount.id,
      returnTo: urlBuilder.appAccount.absolute({ accountId: managedAccount.id }),
      returnLabel: 'Account details',
    });
  } else if (legacyGoalId) {
    detailsUrl = urlBuilder.appPerformance.absolute({ 'settings[goal_ids][]': legacyGoalId.toString() });
  }

  return (
    <PerformanceSection
      balance={balance}
      earned={earned}
      detailsUrl={detailsUrl}
      timeSeries={performanceHistory.timeSeries}
      isLoading={isLoadingWithPreviousData}
      dateRangeOption={dateRangeOption}
      dateRangeOptions={DateRangeOptions.standard}
      onDateRangeChange={setDateRangeOption}
      disclosures={{
        recencyDescription: performanceHistory.recencyDescription,
        performanceDisclosure: data.disclosures.performanceDisclosure,
      }}
    />
  );
};
